import { LOGS_TYPES } from "../../../log/log-service";
import { useOrgData } from "../../../../contexts/OrgDataContext";
import { useJournal } from "../../../../contexts/JournalContext";
import { JOURNAL_DISPLAY_NON_CONFORMITY_ONLY } from "../../../journal/journal-service";

export const LISI_LOG_PIECE_OVEN = 400;
export const LISI_ALERT_LUBRICATION = 450;
export const LISI_ALERT_MISSING_SAFETY_STAND = 451;
export const LISI_ALERT_OVEN_NOT_EMPTY = 452;
export const LISI_ALERT_MISSING_CLAMPING_BRACKETS = 453;
export const LISI_ALERT_LOCKING_SCREWS = 454;
export const LISI_ALERT_CONTACT_SHIMS = 455;

export const detectLISIPatternsInLogs = (originalLogs, patterns, patternExecutions) => {
  const detectedPatterns = [];
  if (!Array.isArray(originalLogs) || !Array.isArray(patternExecutions)) return detectedPatterns;

  // Optional: build a map to quickly find a pattern by id (can be useful as fallback)
  const patternsById = new Map((patterns || []).map((p) => [String(p.id), p]));

  // Precompute log timestamps (in seconds) once
  const logsWithTs = originalLogs.map((log) => {
    const tsMs = new Date(log.time).getTime();
    return { log, tsSec: Math.floor(tsMs / 1000) };
  });

  for (const exec of patternExecutions) {
    const pattern = exec?.pattern || patternsById.get(String(exec?.pattern?.id || exec?.pattern_id)) || null;
    if (!pattern || !pattern.is_active) continue;

    const startSec = Number(exec.start_date);
    const stopSec = Number(exec.stop_date);
    if (!Number.isFinite(startSec) || !Number.isFinite(stopSec) || stopSec < startSec) continue;

    // All logs whose date is within [start_date, stop_date]
    const executionLogs = logsWithTs
      .filter(({ tsSec }) => tsSec >= startSec && tsSec <= stopSec)
      .map(({ log }) => log);

    // Use the execution itself as the detected pattern
    detectedPatterns.push({
      type: LOGS_TYPES.PATTERN,
      time: new Date(stopSec * 1000).toISOString(),
      pattern_id: pattern.id,
      pattern_name: pattern.name,
      pattern_data: pattern.pattern_data ?? [],
      pattern_execution: exec ?? [], // Keep original execution data
      pattern_logs: executionLogs,
      percentage: 100,
    });
  }

  return detectedPatterns;
};

export const addPatternExecutionsToLogs = (logs, patternExecutions) => {
  return logs.map((log) => {
    const { type, time } = log;
    const tsMs = new Date(log?.time).getTime() / 1000;

    const patternExecution = patternExecutions
      .find(({ start_date, stop_date }) => log.type !== LOGS_TYPES.ALERT && tsMs >= start_date && tsMs <= stop_date) ?? null;

    let patternExecutionName = undefined;
    let patternData = {};

    let isOvenTimeTooShort = false, isOvenTimeTooLong = false, isTransferTimeTooLong = false;
    if (patternExecution !== null) {
      patternData = patternExecution?.pattern?.pattern_data.reduce((acc, { name, value }) => ({ ...acc, [name]: value }), {});
      patternExecutionName = patternExecution.pattern.name;

      isOvenTimeTooShort = log?.data_oven_time < parseInt(patternData.oven_time) - parseInt(patternData.oven_tolerance);
      isOvenTimeTooLong = parseInt(patternData.oven_time) + parseInt(patternData.oven_tolerance) < log?.data_oven_time;
      isTransferTimeTooLong = parseInt(patternData.transfer_time) + parseInt(patternData.transfer_tolerance) < log?.data_transfer_time;
    }
    const pieceIsOk = !isOvenTimeTooShort && !isOvenTimeTooLong && !isTransferTimeTooLong;

    return {
      ...log,
      // General data
      date: log?.time.split('T')[0],
      hour: log?.time.split('T')[1].split('.')[0],
      piece_id: log?.data_piece_id ? log?.data_piece_id : '-',
      process_name: patternExecutionName ? patternExecutionName : '(No process)',
      // Oven data
      oven_time: log?.data_oven_time ? Math.round(log?.data_oven_time / 60 * 10) / 10 : '-',
      theoretical_oven_time: patternData.oven_time ? Math.round(patternData.oven_time / 60 * 10) / 10 : '-',
      theoretical_oven_tolerance: patternData.oven_tolerance ? Math.round(patternData.oven_tolerance / 60 * 10) / 10 : '-',
      oven_time_too_short: isOvenTimeTooShort ? 'YES' : 'NO',
      oven_time_too_long: isOvenTimeTooLong ? 'YES' : 'NO',
      // Transfer data
      transfer_oven_hit_time: log?.data_transfer_time ? log?.data_transfer_time : '-',
      theoretical_transfer_oven_hit_time: patternData.transfer_time ? patternData.transfer_time : '-',
      theoretical_transfer_oven_hit_tolerance: patternData.transfer_tolerance ? patternData.transfer_tolerance : '-',
      transfer_oven_hit_time_too_long: isTransferTimeTooLong ? 'YES' : 'NO',
      transfer_hit_cut_time: log?.data_transfer_hit_cut_time ? log?.data_transfer_hit_cut_time : '-',
      // Global
      total_time: log?.data_total_time ? log?.data_total_time : '-',
      piece_ok: patternExecutionName ? (pieceIsOk ? 'YES' : 'NO') : '-',
      correct_bin: log?.data_correct_bin ? (log?.data_correct_bin ? 'YES' : 'NO') : '-',
    };
  });
};